import React, { useState, Fragment } from 'react';
import { Building2, Package, FileText, Users, BarChart3, Settings, LogOut, Calendar as CalendarIcon, ContactRound, ChevronLeft, ChevronRight, ChevronDown, Moon, Sun, ClipboardCheck, Shield, ScrollText, LogIn, Bell, Database, Download, CalendarDays, Bug, Stethoscope, HardDrive, CloudCog } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import useAuthStore from '../stores/authStore';
import { useSystemLogo } from '../hooks/useSystemLogo';
import { useSidebar } from '../contexts/SidebarContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { RoleBadge } from './RoleBadge';
import { NotificationBell } from './notifications';
import { APP_VERSION } from '../config/version';

export const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, podeVerMenu } = useAuthStore();
  const { logoUrl, logoMiniUrl, nomeSistema, isLoading } = useSystemLogo();
  const { isCollapsed, toggle } = useSidebar();
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  // Estado para controlar submenus expandidos
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});

  const toggleMenu = (menuId: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  // Verifica se o usuário é MASTER
  const isMaster = user?.role === 'MASTER';

  // Mapeamento de menu ID para código de tela (Epic 27)
  // Baseado nos códigos reais do banco de dados
  const menuTelaMap: Record<string, string> = {
    'calendario': 'CALENDARIO_VIEW',
    'contagens': 'CONTAGENS_LIST',
    'clientes': 'CLIENTES_LIST',
    'hospitais': 'HOSPITAIS_LIST',
    'conferencia': 'CONFERENCIA_VIEW',
    'relatorios': 'RELATORIOS_VIEW',
    'usuarios': 'USUARIOS_LIST',
    'configuracoes': 'CONFIGURACOES_VIEW',
    'controle-acesso': 'CONTROLE_ACESSO_VIEW',
    'logs-parent': 'LOGS_VIEW',
    'logs': 'LOGS_VIEW',
    'logs-access': 'LOGS_VIEW',
    // Mapeamentos adicionados - Epic 27 completo
    'escopo-contagem': 'ESCOPO_CONTAGEM_VIEW',
    'downloads': 'DOWNLOADS_VIEW',
    'config-sistema-parent': 'CONFIGURACOES_VIEW',
  };

  // Helper: Verifica se menu deve ser exibido
  const deveExibirMenu = (menuId: string): boolean => {
    const codigoTela = menuTelaMap[menuId];
    if (!codigoTela) return true; // Se não tem mapeamento, exibe por padrão

    // Para todos os menus, verifica permissão visivel_menu
    return podeVerMenu(codigoTela);
  };

  // Menu organizado por seções (Sprint 3: reorganizado + Epic 27: filtrado por permissões)
  const menuSections = [
    {
      items: [
        { id: 'calendario', label: 'Calendário', icon: CalendarIcon, path: '/cloud/calendario' },
        { id: 'contagens', label: 'Contagens', icon: FileText, path: '/cloud/contagens' }
      ]
    },
    {
      items: [
        { id: 'hospitais', label: 'Hospitais', icon: Building2, path: '/cloud/hospitais' },
        { id: 'clientes', label: 'Clientes', icon: Users, path: '/cloud/clientes' },
        { id: 'conferencia', label: 'Conferência', icon: ClipboardCheck, path: '/cloud/conferencia' },
        { id: 'escopo-contagem', label: 'Escopo Contagem', icon: CalendarDays, path: '/cloud/escopo-contagem' }
      ]
    },
    {
      items: [
        { id: 'relatorios', label: 'Relatórios', icon: BarChart3, path: '/cloud/relatorios' }
      ]
    },
    {
      items: [
        // Controle de Acesso - Baseado em permissão
        { id: 'controle-acesso', label: 'Controle de Acesso', icon: Shield, path: '/cloud/controle-acesso' },
        // Menu pai Logs com submenus - Baseado em permissão (Epic 38)
        {
          id: 'logs-parent',
          label: 'Logs',
          icon: ScrollText,
          isParent: true,
          subItems: [
            { id: 'logs', label: 'Logs do Sistema', icon: ScrollText, path: '/cloud/logs' },
            { id: 'logs-access', label: 'Logs de Acesso', icon: LogIn, path: '/cloud/logs/access' },
            // N129 fase 2: leitura MASTER-only no backend (T30), some do menu para os demais
            ...(isMaster
              ? [
                  { id: 'diagnosticos', label: 'Diagnosticos do App', icon: Stethoscope, path: '/cloud/diagnosticos' },
                  // N226: idem (tempo do servidor e de cada sincronizacao do app)
                  { id: 'diagnostico-nuvem', label: 'Diagnóstico Nuvem/App', icon: CloudCog, path: '/cloud/logs/diagnostico-nuvem' },
                ]
              : [])
          ]
        },
        // Story 2.39: Bug Reports OCULTADO do menu (decisao de produto).
        // Codigo + rota + dados preservados — apenas link da sidebar removido.
        // Para reativar, descomentar a linha abaixo:
        // ...(isMaster ? [{ id: 'bug-reports', label: 'Bug Reports', icon: Bug, path: '/cloud/bug-reports' }] : []),
        { id: 'usuarios', label: 'Usuários', icon: Users, path: '/cloud/usuarios' },
        { id: 'downloads', label: 'Downloads', icon: Download, path: '/cloud/downloads' },
        // Configurações de Sistema - visibilidade controlada por CONFIGURACOES_VIEW (visivel_menu)
        {
          id: 'config-sistema-parent',
          label: 'Config. Sistema',
          icon: Settings,
          isParent: true,
          subItems: [
            { id: 'config-sistema', label: 'Personalização', icon: Database, path: '/cloud/configuracoes?section=sistema' },
            { id: 'config-motivo', label: 'Motivo/Responsável', icon: FileText, path: '/cloud/configuracoes?section=motivo' },
            { id: 'config-classificacao', label: 'Classificação Documento', icon: FileText, path: '/cloud/configuracoes?section=classificacao-documento' },
            // ⛔ N194 — O MENU DE VERDADE E ESTE, e nao o array `sections` de
            // ConfiguracoesPage.tsx. Aquele alimenta uma barra lateral interna
            // que hoje esta OCULTA para todas as secoes
            // (`showInternalSidebar` e false), entao registrar a secao la e
            // necessario mas NAO suficiente: sem esta linha, a tela existe e so
            // e alcancavel digitando ?section=... na URL.
            { id: 'config-armazenamento', label: 'Configuração Armazenamento', icon: HardDrive, path: '/cloud/configuracoes?section=armazenamento' }
          ]
        },
        // Configurações de Usuário
        {
          id: 'config-usuario-parent',
          label: 'Config. Usuário',
          icon: ContactRound,
          isParent: true,
          subItems: [
            { id: 'config-perfil', label: 'Perfil', icon: Users, path: '/cloud/configuracoes?section=perfil' },
            { id: 'config-seguranca', label: 'Segurança', icon: Shield, path: '/cloud/configuracoes?section=seguranca' }
          ]
        }
      ]
    }
  ].map(section => ({
    ...section,
    items: section.items.filter(item => deveExibirMenu(item.id))
  })).filter(section => section.items.length > 0); // Remove seções vazias

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
  };

  const handleNavigate = (path: string) => {
    // #18: marca navegacao vinda do MENU. O Calendário usa isso para abrir no
    // mês vigente (em vez do último mês visualizado). O "voltar" de um
    // agendamento navega com selectedDate e NÃO passa por aqui, preservando o #3.
    navigate(path, { state: { fromMenu: true } });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderMenuItem = (item: any) => {
    const Icon = item.icon;
    // Verifica se o item é pai e se algum filho está ativo ou se o próprio item está ativo
    const isActive = location.pathname === item.path;
    const isParentActive = item.subItems?.some((sub: any) => 
      location.pathname === sub.path || location.search === sub.path.split('?')[1]
    );
    // Para links com query params, precisamos verificar pathname + search
    const currentFullPath = location.pathname + location.search;
    
    // Se é um menu pai com submenus
    if (item.isParent && item.subItems) {
      const isExpanded = expandedMenus[item.id];
      const isAnySubActive = item.subItems.some((sub: any) => {
         // Check exact match for query params or path
         if (sub.path.includes('?')) {
             return currentFullPath === sub.path;
         }
         return location.pathname === sub.path;
      });

      // QUANDO COLAPSADO: Usa HeadlessUI Menu (popover)
      if (isCollapsed) {
        return (
          <Menu key={item.id} as="div" className="relative">
            {({ open }) => (
              <>
                <Menu.Button
                  className={`w-full flex items-center justify-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-300 ${
                    isAnySubActive
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                  aria-label={item.label}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                </Menu.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute left-full top-0 ml-2 w-56 origin-left bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 z-[200] focus:outline-none">
                    {/* Header */}
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {item.label}
                      </p>
                    </div>

                    {/* Submenu Items */}
                    {item.subItems.map((subItem: any) => {
                      const SubIcon = subItem.icon;
                      const isSubActive = subItem.path.includes('?') 
                        ? currentFullPath === subItem.path
                        : location.pathname === subItem.path;
                        
                      return (
                        <Menu.Item key={subItem.id}>
                          {({ active }) => (
                            <button
                              onClick={() => handleNavigate(subItem.path)}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                                isSubActive
                                  ? 'bg-blue-600 text-white'
                                  : active
                                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                                  : 'text-gray-700 dark:text-gray-300'
                              }`}
                            >
                              <SubIcon className="w-4 h-4 flex-shrink-0" />
                              <span className="font-medium">{subItem.label}</span>
                            </button>
                          )}
                        </Menu.Item>
                      );
                    })}
                  </Menu.Items>
                </Transition>
              </>
            )}
          </Menu>
        );
      }

      // QUANDO EXPANDIDO: Mantém comportamento accordion atual
      return (
        <div key={item.id} className="relative group">
          {/* Botão do menu pai */}
          <button
            onClick={() => toggleMenu(item.id)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-300 ${
              isAnySubActive
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            aria-label={item.label}
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium whitespace-nowrap flex-1 text-left">
              {item.label}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
          </button>

          {/* Submenus - aparecem quando expandido */}
          {isExpanded && (
            <div className="mt-1 ml-4 space-y-0.5 border-l-2 border-gray-200 dark:border-gray-700 pl-2">
              {item.subItems.map((subItem: any) => {
                const SubIcon = subItem.icon;
                const isSubActive = subItem.path.includes('?') 
                        ? currentFullPath === subItem.path
                        : location.pathname === subItem.path;
                return (
                  <button
                    key={subItem.id}
                    onClick={() => handleNavigate(subItem.path)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300 text-sm ${
                      isSubActive
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <SubIcon className="w-4 h-4 flex-shrink-0" />
                    <span className="font-medium">{subItem.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Menu item normal (sem submenus)
    return (
      <div key={item.id} className="relative group">
        <button
          onClick={() => handleNavigate(item.path)}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-300 ${
            isActive
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
          } ${isCollapsed ? 'justify-center' : ''}`}
          aria-label={item.label}
        >
          {/* Ícone com tamanho FIXO em ambos os estados */}
          <Icon className="w-5 h-5 flex-shrink-0" />

          {/* Label com transição suave - ocupa 0px quando colapsado */}
          <span className={`text-sm font-medium whitespace-nowrap transition-all duration-300 ${
            isCollapsed ? 'w-0 opacity-0 overflow-hidden' : 'w-auto opacity-100'
          }`}>
            {item.label}
          </span>
        </button>

        {/* Tooltip otimizado - aparece ao passar mouse sobre ícone */}
        {isCollapsed && (
          <div
            className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-2 bg-gray-900 dark:bg-gray-800 text-white text-sm font-medium rounded-md shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[200] pointer-events-none"
            role="tooltip"
          >
            {item.label}
            <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45"></div>
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`${
        isCollapsed ? 'w-16' : 'w-56'
      } bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 shadow-sm flex flex-col h-screen fixed left-0 top-0 z-50 transition-all duration-300 ease-in-out overflow-visible`}
      style={{
        transitionProperty: 'width',
        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    >
      {/* Logo Header - Altura fixa com container interno para alinhamento perfeito */}
      <div className={`px-4 border-b-2 border-gray-300 dark:border-gray-700 bg-gradient-to-b from-blue-50 to-white dark:from-gray-800 dark:to-gray-900 relative transition-all duration-300 ${isCollapsed ? 'h-[80px]' : 'h-[160px]'}`}>
        {/* Toggle Button - posição absoluta sem afetar layout */}
        <button
          onClick={toggle}
          className="absolute top-3 right-2 p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors z-10"
          aria-label={isCollapsed ? 'Expandir menu' : 'Colapsar menu'}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>

        {/* Container interno com altura total para centralização perfeita */}
        <div className={`flex flex-col items-center h-full w-full transition-all duration-300 ${
          isCollapsed ? 'justify-center pt-8' : 'justify-center pt-2'
        }`}>

          {/* Logo - tamanho FIXO em ambos os estados - só mostra se houver logo configurado */}
          {isLoading ? (
            <div className={`${isCollapsed ? 'h-10 w-10' : 'h-12 w-40'} bg-gray-300 dark:bg-gray-700 animate-pulse rounded transition-all duration-300`} />
          ) : (
            (isCollapsed ? logoMiniUrl : logoUrl) && (
              <img
                src={isCollapsed ? logoMiniUrl : logoUrl}
                alt={nomeSistema}
                className={`${isCollapsed ? 'h-10' : 'h-12'} w-auto object-contain transition-all duration-300 opacity-100`}
              />
            )
          )}

          {/* Nome do Sistema - height: 0 quando colapsado para NÃO ocupar espaço */}
          <div className={`flex flex-col items-center text-center overflow-hidden transition-all duration-300 ${
            isCollapsed
              ? 'h-0 opacity-0'
              : 'h-auto opacity-100 mt-3 pb-2'
          }`}>
            <span className="text-xl font-bold text-gray-900 dark:text-white whitespace-nowrap">
              {nomeSistema}
            </span>
            <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mt-0.5 whitespace-nowrap">
              Painel Administrativo
            </span>
            {/* Versão da aplicação (APP_VERSION ja inclui prefixo 'v') */}
            <span className="text-[9px] text-gray-500 dark:text-gray-500 font-mono mt-0.5 whitespace-nowrap">
              {APP_VERSION}
            </span>
          </div>
        </div>
      </div>

      {/* Navigation - Menu com seções separadas - overflow removido para permitir tooltips */}
      <nav className="flex-1 px-3 pt-4 pb-2 transition-all duration-300">
        {menuSections.map((section, sectionIndex) => (
          <div key={sectionIndex}>
            <div className="space-y-0.5">
              {section.items.map(renderMenuItem)}
            </div>
            {sectionIndex < menuSections.length - 1 && (
              <div className={`my-2 border-t border-gray-200 dark:border-gray-700 ${isCollapsed ? 'mx-1' : ''}`}></div>
            )}
          </div>
        ))}
      </nav>

      {/* User Footer - padding consistente */}
      <div className="px-3 py-3 border-t border-gray-200 dark:border-gray-700 transition-all duration-300">
        {/* Dark Mode Toggle Button - Above User Profile */}
        <div className="relative group mb-3">
          <button
            onClick={toggleDarkMode}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 ${
              isCollapsed ? 'justify-center' : ''
            }`}
            aria-label={isDarkMode ? 'Desativar modo escuro' : 'Ativar modo escuro'}
          >
            {/* Ícone com tamanho FIXO em ambos os estados */}
            {isDarkMode ? (
              <Sun className="w-5 h-5 flex-shrink-0" />
            ) : (
              <Moon className="w-5 h-5 flex-shrink-0" />
            )}

            {/* Label com transição suave - ocupa 0px quando colapsado */}
            <span className={`text-sm font-medium whitespace-nowrap transition-all duration-300 ${
              isCollapsed ? 'w-0 opacity-0 overflow-hidden' : 'w-auto opacity-100'
            }`}>
              {isDarkMode ? 'Modo Claro' : 'Modo Escuro'}
            </span>
          </button>

          {/* Tooltip - aparece ao passar mouse quando colapsado */}
          {isCollapsed && (
            <div
              className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-2 bg-gray-900 dark:bg-gray-800 text-white text-sm font-medium rounded-md shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[200] pointer-events-none"
              role="tooltip"
            >
              {isDarkMode ? 'Modo Claro' : 'Modo Escuro'}
              <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45"></div>
            </div>
          )}
        </div>

        {/* Epic 47: Notification Bell - visivel para quem tem acesso a CONFIGURACOES_VIEW */}
        {podeVerMenu('CONFIGURACOES_VIEW') && (
          <div className="mb-3">
            <NotificationBell isCollapsed={isCollapsed} isMaster={isMaster} />
          </div>
        )}

        {user && (
          <>
            {isCollapsed ? (
              // Versão colapsada - Avatar apenas
              <div className="relative group">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center font-bold text-white text-sm mx-auto mb-2 cursor-pointer">
                  {getInitials(user.email || 'Admin User')}
                </div>

                {/* Tooltip do usuário - Aparece ao passar o mouse */}
                <div
                  className="absolute left-full bottom-12 ml-2 px-3 py-2 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded-md shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[200] pointer-events-none"
                  role="tooltip"
                >
                  <p className="font-medium">{user.nome || user.username || 'Usuário'}</p>
                  <p className="text-xs text-gray-300 mb-1">{user.email || 'sem email'}</p>
                  <RoleBadge role={user.role} size="sm" />
                  <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45"></div>
                </div>
              </div>
            ) : (
              // Versão expandida - Avatar + Nome + Badge Perfil
              <div className="flex items-center gap-2 mb-2 opacity-100 transition-opacity duration-300">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center font-bold text-white text-sm">
                  {getInitials(user.nome || user.username || user.email || 'U')}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{user.nome || user.username || 'Usuário'}</p>
                  <RoleBadge role={user.role} size="sm" className="mt-1" />
                </div>
              </div>
            )}

            {/* Logout Button */}
            <div className="relative group">
              <button
                onClick={handleLogout}
                className={`w-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 py-2 rounded-lg flex items-center ${isCollapsed ? 'justify-center' : 'justify-center gap-2'} transition-colors`}
                aria-label="Sair"
              >
                {/* Ícone com tamanho FIXO */}
                <LogOut className="w-5 h-5" />
                {!isCollapsed && <span className="text-sm font-medium">Sair</span>}
              </button>

              {/* Tooltip Sair - Aparece ao passar o mouse */}
              {isCollapsed && (
                <div
                  className="absolute left-full bottom-0 ml-2 px-3 py-2 bg-gray-900 dark:bg-gray-800 text-white text-sm font-medium rounded-md shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[200] pointer-events-none"
                  role="tooltip"
                >
                  Sair
                  <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45"></div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </aside>
  );
};
